import { jsPDF } from 'jspdf';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FormProgress, SecondContractor } from '../types/form';
import { contractTemplate } from './contractTemplate';
import { numberToWords } from './numberToWords';

const cleanContent = (text: string) =>
  text
    .split('\n')
    .filter((line, index) => !(index === 0 && line.match(/^\d+\s*-/)))
    .join('\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

export async function generateDualContract(
  formData: FormProgress['data'],
  secondContractor: SecondContractor
): Promise<Blob> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  doc.setFont('helvetica');

  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;

  const addFooter = (pageNumber: number) => {
    const footerY = pageHeight - 30;
    doc.addImage('https://i.imgur.com/J9lB0MV.jpg', 'PNG', pageWidth - 50, footerY, 30, 30 * 0.3);
    doc.setFontSize(8);
    doc.text(`Página ${pageNumber}`, 20, footerY + 10, { align: 'left' });
  };

  const addLogo = (x: number, y: number, width: number) => {
    try {
      doc.addImage('https://i.imgur.com/J9lB0MV.jpg', 'PNG', pageWidth - 50, y, width, width * 0.3);
      return y + width * 0.3 + 5;
    } catch (error) {
      console.error('Error adding logo:', error);
      return y;
    }
  };

  const addMultiLineText = (
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    fontSize = 10,
    fontStyle: 'normal' | 'bold' = 'normal'
  ): number => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);

    const lines = doc.splitTextToSize(text.trim().replace(/\n{2,}/g, '\n'), maxWidth);
    const lineHeight = fontSize * 0.6;

    lines.forEach((line: string) => {
      if (y + lineHeight > 270) {
        const pageNumber = doc.internal.getNumberOfPages();
        addFooter(pageNumber);
        doc.addPage();
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        y = 20;
      }
      doc.text(line, x, y);
      y += lineHeight;
    });

    return y;
  };

  const addSection = (title: string, content: string, yPos: number, fontSize = 10): number => {
    const titleOnly = title.split('\n')[0];
    yPos = addMultiLineText(titleOnly, 20, yPos, 170, fontSize + 2, 'bold');
    yPos += 3;
    yPos = addMultiLineText(cleanContent(content), 20, yPos, 170, fontSize, 'normal');
    return yPos + 6;
  };

  const addPage = (): number => {
    const pageNumber = doc.internal.getNumberOfPages();
    addFooter(pageNumber);
    doc.addPage();
    return 20;
  };

  let yPos = 20;
  yPos = addLogo(pageWidth - 50, yPos, 30);
  yPos += 5;

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('CONTRATO DE PRESTAÇÃO DE SERVIÇOS', 105, yPos, { align: 'center' });
  yPos += 15;

  doc.setFontSize(12);
  doc.text('IDENTIFICAÇÃO DAS PARTES CONTRATADAS', 20, yPos);
  yPos += 7;

  const companyInfo = `CONTRATADA: ECOENERGI SOLAR, PESSOA JURÍDICA DE DIREITO PRIVADO, INSCRITA NO CNPJ SOB O N° 12.276.329.0001-69, COM SEDE NA RUA DEPUTADO JÚLIO CÉSAR PAULINO MAIA - N°1346- CENTRO, NA CIDADE DE SANTA RITA DO PARDO - MS, COM CEP: 79690-000, NESTE ATO REPRESENTADA POR DIOGO CASTRO ALVES RODRIGUES, INSCRITO NO CPF SOB O N° 038.281.431-21.`;
  yPos = addMultiLineText(companyInfo, 20, yPos, 170);
  yPos += 4;

  // First contractor
  const maritalStatusMap: Record<string, string> = {
    Single: 'SOLTEIRO(A)',
    Married: 'CASADO(A)',
    Divorced: 'DIVORCIADO(A)',
    Widowed: 'VIÚVO(A)'
  };
  const client1Marital = maritalStatusMap[formData.customerInfo?.maritalStatus || ''] || 'CASADO(A)';
  const client1Info = `CONTRATANTE 1: ${(formData.customerInfo?.fullName || '').toUpperCase()}, PORTADOR(A) DO CPF/CNPJ ${formData.customerInfo?.cpf} E RG ${formData.customerInfo?.rg} ${(formData.customerInfo?.issuingAuthority || '').toUpperCase()}, ${(formData.customerInfo?.nationality || '').toUpperCase()}, ${client1Marital}, ${(formData.customerInfo?.profession || '').toUpperCase()}, RESIDENTE E DOMICILIADO(A) EM ${(formData.installationLocation?.street || '').toUpperCase()}, ${formData.installationLocation?.number}, ${(formData.installationLocation?.neighborhood || '').toUpperCase()}, ${(formData.installationLocation?.city || '').toUpperCase()}/${formData.installationLocation?.state}, CEP ${formData.installationLocation?.zipCode}, TELEFONE ${formData.customerInfo?.phone}, EMAIL ${formData.customerInfo?.email}.`;
  yPos = addMultiLineText(client1Info, 20, yPos, 170);
  yPos += 4;

  // Second contractor
  const loc = formData.installationLocation;
  const client2Marital = maritalStatusMap[secondContractor.maritalStatus || ''] || '';
  const addressText = secondContractor.sameAddress
    ? `RESIDENTE E DOMICILIADO(A) EM ${(loc?.street || '').toUpperCase()}, ${loc?.number}, ${(loc?.neighborhood || '').toUpperCase()}, ${(loc?.city || '').toUpperCase()}/${loc?.state}, CEP ${loc?.zipCode}`
    : '';
  const client2Info = `CONTRATANTE 2: ${secondContractor.fullName.toUpperCase()}, PORTADOR(A) DO CPF/CNPJ ${secondContractor.cpf} E RG ${secondContractor.rg} ${secondContractor.issuingAuthority.toUpperCase()}, ${secondContractor.nationality.toUpperCase()}, ${client2Marital}, ${addressText}${secondContractor.email ? `, EMAIL ${secondContractor.email.toUpperCase()}` : ''}.`;
  yPos = addMultiLineText(client2Info, 20, yPos, 170);
  yPos += 6;

  // Object clause with network electrical services included
  const systemPower = (
    ((formData.technicalConfig?.solarModules.power || 0) *
      (formData.technicalConfig?.solarModules.quantity || 0)) /
    1000
  ).toFixed(2);
  const requiredArea = (formData.technicalConfig?.solarModules.quantity || 0) * 3;

  const clientDutiesContent = contractTemplate.clauses.clientDuties.replace(
    '4.6 Disponibilizar área mínima de 60m² necessária para instalação do projeto;',
    `4.6 Disponibilizar área mínima de ${requiredArea}m² necessária para instalação do projeto;`
  );

  const objectContent = `1.1 A CONTRATADA compromete-se a prestar os serviços de instalação do sistema fotovoltaico, bem como tratativas com a concessionária de distribuição de energia elétrica. O sistema presente tem como potência ${systemPower}kWp (Quilo Watts Pico).

Dentre os principais materiais estão inclusos:
- ${formData.technicalConfig?.inverter1.quantity} Inversor(es) ${formData.technicalConfig?.inverter1.brand} de ${formData.technicalConfig?.inverter1.power}kW de Potência de Saída${formData.technicalConfig?.inverter2 ? `\n- ${formData.technicalConfig.inverter2.quantity} Inversor(es) ${formData.technicalConfig.inverter2.brand} de ${formData.technicalConfig.inverter2.power}kW de Potência de Saída` : ''}
- ${formData.technicalConfig?.solarModules.quantity} Módulos Solares ${formData.technicalConfig?.solarModules.brand} ${formData.technicalConfig?.solarModules.power}W de Potência
- Estrutura para fixação dos módulos instalados em ${formData.technicalConfig?.installationType === 'Roof' ? 'Telhado' : 'Solo'}

Parágrafo 1º: A CONTRATADA poderá, caso necessário, substituir marcas, modelos ou potências dos equipamentos especificados (como módulos, inversores e demais componentes), desde que os itens substituídos possuam qualidade técnica equivalente ou superior. Tal substituição poderá ocorrer por motivos como indisponibilidade em estoque, descontinuidade do fornecedor ou necessidade de manutenção do cronograma da obra.

Parágrafo 2º: Todos os serviços de rede elétrica necessários para deixar o kit solar em funcionamento estão inclusos neste contrato, incluindo, mas não se limitando a, passagem de cabos, conexões, adequações no quadro de distribuição e demais intervenções na rede elétrica do imóvel que se façam necessárias para o pleno funcionamento do sistema fotovoltaico.`;
  yPos = addSection('1 - DO OBJETO DO PRESENTE CONTRATO', objectContent, yPos);

  if (yPos > 240) yPos = addPage();

  // Payment
  const installments = formData.financialTerms?.installments || [];
  let paymentTerms = '';
  if (installments.length === 1) {
    paymentTerms = `Pagamento único no valor de R$ ${installments[0].amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} a ser pago via ${installments[0].method} com vencimento em ${format(installments[0].dueDate, 'dd/MM/yyyy')}.`;
  } else {
    paymentTerms = installments
      .map(
        (inst, index) =>
          `Parcela ${index + 1}: R$ ${inst.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} via ${inst.method} com vencimento em ${format(inst.dueDate, 'dd/MM/yyyy')}`
      )
      .join('\n');
  }

  const priceContent = `2.1 OS CONTRATANTES pagarão à CONTRATADA, pelos serviços descritos na cláusula primeira deste compromisso, a quantia de R$ ${formData.financialTerms?.totalAmount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}, referentes aos materiais, mão de obra, engenharia, serviços de projetos e aprovações.

2.2 Fica acordado o pagamento nas seguintes condições:
${paymentTerms}

2.3 O não cumprimento pelos CONTRATANTES das datas e prazos avençados nesta cláusula os constituirá em mora, independentemente de interpelação da CONTRATADA, nos termos do artigo 397 do Código Civil. Ainda, acarretará aos CONTRATANTES multa equivalente a 2% do valor total, acrescidos de juros de mora de 3% ao mês, contados a partir do inadimplemento da obrigação.

2.4 Quaisquer taxas, custas, ou acréscimos que surjam em decorrência de alteração da avença acima, ou de prazos de boletos, ou mesmo de renegociações, serão de responsabilidade dos CONTRATANTES.`;
  yPos = addSection('2 - PREÇO E FORMA DE PAGAMENTO', priceContent, yPos);

  const inverter1Warranty = `${formData.technicalConfig?.inverter1.warrantyPeriod} (${numberToWords(formData.technicalConfig?.inverter1.warrantyPeriod || 0)}) anos contra defeitos de fabricação e perda de eficiência energética`;
  const inverter2Warranty = formData.technicalConfig?.inverter2
    ? `\n   - ${formData.technicalConfig.inverter2.warrantyPeriod} (${numberToWords(formData.technicalConfig.inverter2.warrantyPeriod || 0)}) anos contra defeitos de fabricação e perda de eficiência energética`
    : '';
  const warrantyContent = contractTemplate.clauses.warranties.replace(
    'b) Inversores:\n   - 10 (dez) anos contra defeitos de fabricação e perda de eficiência energética',
    `b) Inversores:\n   - ${inverter1Warranty}${inverter2Warranty}`
  );

  const deadlinesContent = contractTemplate.clauses.deadlines.replace(
    '5.1 O cronograma será avaliado conforme liberação das atividades. Prevê-se 50 dias corridos após pagamento dos materiais para conclusão da obra.',
    `5.1 O cronograma será avaliado conforme liberação das atividades. Prevê-se ${formData.technicalConfig?.installationDays} (${numberToWords(formData.technicalConfig?.installationDays || 0)}) dias corridos após pagamento dos materiais para conclusão da obra.`
  );

  // Replace "O CONTRATANTE" with "OS CONTRATANTES" in remaining clauses
  const adaptToDual = (text: string): string =>
    text
      .replace(/O CONTRATANTE\b/g, 'OS CONTRATANTES')
      .replace(/CONTRATANTE:/g, 'CONTRATANTES:');

  Object.entries(contractTemplate.clauses).forEach(([key, content]) => {
    if (!['object', 'price'].includes(key)) {
      if (yPos > 240) yPos = addPage();

      if (key === 'warranties') {
        yPos = addSection('7 - DAS GARANTIAS', cleanContent(warrantyContent), yPos);
      } else if (key === 'clientDuties') {
        yPos = addSection('4 - DOS DEVERES E OBRIGAÇÕES DOS CONTRATANTES', cleanContent(adaptToDual(clientDutiesContent)), yPos);
      } else if (key === 'deadlines') {
        yPos = addSection('5 - PRAZOS E CRONOGRAMA', cleanContent(deadlinesContent), yPos);
      } else if (key === 'contractorDuties') {
        yPos = addSection('3 - DOS DEVERES E OBRIGAÇÕES DA CONTRATADA', cleanContent(content), yPos);
      } else if (key === 'termination') {
        yPos = addSection('6 - DA RESCISÃO E DAS PENALIDADES CONTRATUAIS', cleanContent(adaptToDual(content)), yPos);
      } else if (key === 'liabilityOnTariffs') {
        yPos = addSection('8 - DA LIMITAÇÃO DE RESPONSABILIDADE SOBRE TARIFAS E REGULAMENTOS', cleanContent(adaptToDual(content)), yPos);
      } else if (key === 'consumptionProfile') {
        yPos = addSection('9 - DA ALTERAÇÃO DE PERFIL DE CONSUMO E DIMENSIONAMENTO DO SISTEMA', cleanContent(adaptToDual(content)), yPos);
      } else if (key === 'finalProvisions') {
        yPos = addSection('10 - DISPOSIÇÕES FINAIS', cleanContent(adaptToDual(content)), yPos);
      }
    }
  });

  if (yPos > 180) yPos = addPage();
  else yPos += 10;

  yPos += 15;
  const currentDate = format(new Date(), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  doc.text(`${formData.installationLocation?.city}, ${currentDate}`, 185, yPos, { align: 'right' });

  yPos += 15;

  // Three signature lines: CONTRATADA, CONTRATANTE 1, CONTRATANTE 2
  doc.setFontSize(10);
  doc.line(20, yPos, 80, yPos);
  doc.line(95, yPos, 155, yPos);
  doc.line(170, yPos, 200, yPos);

  doc.setFontSize(9);
  doc.text('ECOENERGI SOLAR', 50, yPos + 5, { align: 'center' });
  doc.text(formData.customerInfo?.fullName || '', 125, yPos + 5, { align: 'center' });
  doc.text(secondContractor.fullName, 185, yPos + 5, { align: 'center' });

  doc.text('CNPJ: 12.276.329.0001-69', 50, yPos + 10, { align: 'center' });
  doc.text(`CPF: ${formData.customerInfo?.cpf}`, 125, yPos + 10, { align: 'center' });
  doc.text(`CPF: ${secondContractor.cpf}`, 185, yPos + 10, { align: 'center' });

  yPos += 25;
  doc.setFontSize(10);
  doc.text('TESTEMUNHAS:', 20, yPos);
  doc.line(20, yPos + 10, 90, yPos + 10);
  doc.line(115, yPos + 10, 185, yPos + 10);
  doc.text('Nome:', 20, yPos + 15);
  doc.text('Nome:', 115, yPos + 15);
  doc.text('CPF:', 20, yPos + 20);
  doc.text('CPF:', 115, yPos + 20);

  const pageNumber = doc.internal.getNumberOfPages();
  addFooter(pageNumber);

  return doc.output('blob');
}
